import { gql, useLazyQuery, useQuery, useReactiveVar } from '@apollo/client';
import { Download, Info, IosShare } from '@mui/icons-material';
import { Button, IconButton } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import type React from 'react';
import { useEffect, useState } from 'react';
import BatchDownloadDialog from 'src/components/dialogs/BatchDownloadDialog';
import ConfirmDownloadDialog from 'src/components/dialogs/ConfirmDownloadDialog';
import LoadingDialog from 'src/components/dialogs/LoadingDialog';
import VerticalLine from 'src/components/VerticalLine';
import { CAN_EDIT_SLUG, COLLECTION, MANAGE_GALLERY_SLUG, VIEW_CONTEXT } from 'src/constants';
import { buildDownloadAvailabilityQ } from 'src/dynamoQueries/download';
import { itemPermsQ } from 'src/dynamoQueries/perms';
import DensityToggle from 'src/HomePage/DrawerAndPushedContents/PushedContents/TopPaginationNavBar/DensityToggle/DensityToggle';
import LayoutIcons from 'src/HomePage/DrawerAndPushedContents/PushedContents/TopPaginationNavBar/LayoutIcons/LayoutIcons';
import SelectAllCheckbox from 'src/HomePage/DrawerAndPushedContents/PushedContents/TopPaginationNavBar/SelectAllCheckbox/SelectAllCheckbox';
import SortBy from 'src/HomePage/DrawerAndPushedContents/PushedContents/TopPaginationNavBar/SortBy/SortBy';
import GalleryTopActions from 'src/HomePage/DrawerAndPushedContents/PushedContents/TopPaginationNavBar/TopPaginationActions/GalleryTopActions';
import { getPerm } from 'src/helpers/permissionsHelper';
import { useAssetDownload, useCollectionDownload } from 'src/hooks/download';
import { useCustomToast } from 'src/hooks/menu';
import { useMyPerms } from 'src/hooks/perms';
import viewStateVar from 'src/store/viewState';

interface Permission {
  slug: string;
  granted: boolean;
  [key: string]: string | number | boolean | null | undefined;
}

interface ItemPermissionsData {
  itemPermissions: {
    mediaPermissions: Permission[];
  };
}

interface MyPermissionsData {
  myPermissions: {
    userPerms: Permission[];
  };
}

interface AvailabilityItem {
  formatType: string;
  formatLabel: string;
  available: number;
  total: number;
}

interface AvailabilityData {
  downloadAvailability: AvailabilityItem[];
}

interface AvailabilityMap {
  [key: string]: {
    available: number;
    total: number;
  };
}

interface AvailabilityOption {
  value: string;
  label: string;
}

interface DownloadOptions {
  formatType: string | null;
  fallbackPolicy?: 'SKIP_MISSING' | 'FALLBACK_CHAIN' | 'STRICT';
}

const useStyles = makeStyles((theme: Theme) => ({
  toolBar: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '5px 0px 10px 10px',
  },
  toolBarLeft: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 20,
  },
  toolBarPagination: {
    margin: 'auto',
  },
  toolBarRight: {
    display: 'flex',
    flexDirection: 'row-reverse',
    alignItems: 'center',
  },
  iconButton: {
    lineHeight: '1',
    padding: '8px',
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&.MuiIconButton-root': {
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
  },
  marginIcon: {
    marginTop: '.2rem',
  },
  toggleButton: {
    padding: '8px 14px',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    borderRadius: 4,
    fontSize: 13,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
    '&.active': {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.secondary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.secondary.light,
      },
    },
  },
}));

interface GalleryToolbarProps {
  galleryId: string;
}

const GalleryToolbar = ({ galleryId }: GalleryToolbarProps): React.JSX.Element => {
  const classes = useStyles();
  const { toastr: customToast } = useCustomToast();
  const viewState = useReactiveVar(viewStateVar);
  const isPanelOpen = viewState.isGalleryPanelOpen;

  const togglePanel = () => {
    viewStateVar({
      ...viewStateVar(),
      isGalleryPanelOpen: !isPanelOpen,
    });
  };

  const buildPermsQuery = () => {
    const q = itemPermsQ(true);
    return gql(jsonToGraphQLQuery(q));
  };

  const {
    data: permsData,
    loading: permsLoading,
    error: permsError,
  } = useQuery<ItemPermissionsData>(buildPermsQuery(), {
    skip: !galleryId,
    variables: { globalId: galleryId },
  });

  const { data: myPermsData, loading: myPermsLoading, error: myPermsError } = useMyPerms();
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openBatchSelected, setOpenBatchSelected] = useState(false);
  const [availabilitySelected, setAvailabilitySelected] = useState<AvailabilityMap | null>(null);
  const [availabilityOptions, setAvailabilityOptions] = useState<AvailabilityOption[] | null>(null);

  const {
    downloadGallery,
    initiateDownload,
    galleryReady,
    downloadCount,
    prAvailable,
    prTotal,
    loading,
    error: err,
    setGalleryReady,
  } = useCollectionDownload(galleryId);
  const { handleAssetDownload } = useAssetDownload(false);

  // Fetch availability for selected assets when triggered from the toolbar button
  const [getAvailability, { loading: availabilityLoading }] = useLazyQuery<AvailabilityData>(
    buildDownloadAvailabilityQ(),
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
      onCompleted: (data) => {
        try {
          const list = data?.downloadAvailability || [];
          const map = list.reduce<AvailabilityMap>((acc, item) => {
            const code = String(item.formatType);
            acc[code] = { available: item.available, total: item.total };
            return acc;
          }, {});
          const opts: AvailabilityOption[] = list.map((item) => ({
            value: String(item.formatType),
            label: item.formatLabel || String(item.formatType),
          }));
          setAvailabilitySelected(map);
          setAvailabilityOptions(opts);
        } catch {
          // Availability transform failed; set empty state to avoid misleading UI and stop loading
          setAvailabilitySelected({});
          setAvailabilityOptions([]);
        }
      },
      onError: () => {
        // Availability query failed; set empty state so dialog shows a friendly "no options" message
        setAvailabilitySelected({});
        setAvailabilityOptions([]);
      },
    },
  );

  // Do not early-return before hooks; gate UI later to preserve hooks order

  const perms = permsData?.itemPermissions?.mediaPermissions || [];
  const editPerm = getPerm(perms, CAN_EDIT_SLUG);
  const myPerms = (myPermsData as MyPermissionsData)?.myPermissions?.userPerms || [];
  const manage_gallery_perm = getPerm(myPerms, MANAGE_GALLERY_SLUG);
  // When gallery items are ready, prompt user to confirm download to satisfy browser gesture
  useEffect(() => {
    if (galleryReady && downloadCount > 0) {
      setOpenConfirm(true);
    }
  }, [galleryReady, downloadCount]);

  if (err) {
    customToast.error('Error downloading gallery');
  }

  const gateUI = !(permsLoading || myPermsLoading || permsError || myPermsError);

  return (
    <>
      {gateUI && (
        <div className={classes.toolBar}>
          <div className={classes.toolBarLeft}>
            <SortBy />
            <VerticalLine />
            <SelectAllCheckbox />
            <VerticalLine />
            <GalleryTopActions
              disabled={false}
              downloadGallery={downloadGallery}
              galleryLoading={loading}
              galleryError={err}
            />
          </div>
          {/* toolBarRight uses row-reverse: visual order is reversed from DOM order */}
          <div className={classes.toolBarRight}>
            <Button
              className={`${classes.toggleButton} ${isPanelOpen ? 'active' : ''}`}
              startIcon={<Info fontSize="small" />}
              onClick={togglePanel}
              sx={{ marginRight: 1 }}
            >
              Gallery Info
            </Button>
            <VerticalLine />
            <LayoutIcons context={VIEW_CONTEXT.GALLERY} />
            <VerticalLine />
            <DensityToggle context={VIEW_CONTEXT.GALLERY} />
            {(editPerm?.granted || manage_gallery_perm?.granted) && (
              <>
                <VerticalLine />
                <IconButton
                  size="small"
                  onClick={() =>
                    viewStateVar({
                      ...viewStateVar(),
                      shareDialog: galleryId,
                      shareContent: COLLECTION,
                    })
                  }
                  className={`${classes.iconButton} ${classes.marginIcon}`}
                >
                  <IosShare fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => {
                    const currentViewState = viewStateVar();
                    const selected = currentViewState?.selectedAssets || [];
                    if (selected.length > 0) {
                      // Selected assets: open dialog immediately and fetch availability to show spinner
                      setOpenBatchSelected(true);
                      getAvailability({ variables: { globalIds: selected } });
                    } else {
                      // No selection: prepare entire gallery, confirm will open when ready
                      downloadGallery();
                    }
                  }}
                  className={`${classes.iconButton} ${classes.marginIcon}`}
                >
                  <Download fontSize="small" />
                </IconButton>
              </>
            )}
          </div>
        </div>
      )}
      {/* Confirm entire-gallery proxy-only download to preserve user gesture */}
      <ConfirmDownloadDialog
        isOpen={openConfirm}
        downloadCount={typeof prAvailable === 'number' ? prAvailable : downloadCount}
        available={prAvailable ?? undefined}
        total={prTotal ?? undefined}
        formatLabel={'Proxy'}
        onCancel={() => {
          setOpenConfirm(false);
          setGalleryReady(false);
        }}
        onConfirm={() => {
          initiateDownload({ formatType: 'PR', fallbackPolicy: 'SKIP_MISSING' });
          setOpenConfirm(false);
          setGalleryReady(false);
        }}
      />
      {loading && <LoadingDialog header="Loading Gallery for download..." />}
      {/* Batch dialog for selected assets via toolbar */}
      <BatchDownloadDialog
        isOpen={openBatchSelected}
        availability={availabilitySelected}
        options={availabilityOptions}
        loading={availabilityLoading}
        onConfirm={({ formatType }: DownloadOptions) => {
          const selected = viewStateVar()?.selectedAssets || [];
          // Enforce SKIP_MISSING policy for selected assets from toolbar dialog
          handleAssetDownload(selected, { formatType: formatType ?? 'PR', fallbackPolicy: 'SKIP_MISSING' });
          setOpenBatchSelected(false);
        }}
        onCancel={() => setOpenBatchSelected(false)}
      />
    </>
  );
};

export default GalleryToolbar;
